import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../api/client";

interface SubscriptionInfo {
  plan: "solo" | "pro" | "label" | null;
  status:
    | "trialing"
    | "active"
    | "past_due"
    | "canceled"
    | "incomplete"
    | "incomplete_expired"
    | "unpaid"
    | null;
  hasActiveSubscription: boolean;
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  trialDays: number;
}

/**
 * App-wide banner shown above page content. Three states the user can land in:
 *
 *  1. No active subscription at all (status is null / canceled / unpaid /
 *     incomplete_expired) → show a "pick a plan" prompt that takes them to
 *     /pricing. Most state-changing API endpoints will return 402 in this
 *     case, so the banner doubles as the explanation for why their actions
 *     don't work.
 *
 *  2. `past_due` → Stripe hasn't been able to charge the card. We KEEP the
 *     automation running for a few days (Stripe retries on its own schedule),
 *     but we surface a "update payment method" CTA so the user can fix it
 *     before the grace period ends and we have to cut service.
 *
 *  3. `trialing` with < 3 days left → friendly "your trial ends on …" so
 *     people who plan to cancel aren't surprised by the first charge. We
 *     intentionally don't nag during the first 11 days of trial.
 *
 * The banner short-circuits to null if the query is still loading or
 * everything is healthy, so the dashboard stays clean for normal users.
 */
export default function SubscriptionBanner(): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const subQuery = useQuery({
    queryKey: ["subscription"],
    queryFn: async (): Promise<SubscriptionInfo> => {
      const { data } = await api.get("/api/billing/subscription");
      return data;
    },
    // Poll every 60s while the tab is visible so the banner clears within
    // a minute of the user finishing checkout (no manual refresh needed).
    refetchInterval: 60_000,
    retry: false,
  });

  const portalMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/api/billing/portal");
      return data as { url: string };
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => toast.error(apiError(err)),
  });

  const sub = subQuery.data;
  if (!sub) return null;

  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const formatDate = (iso: string | null): string => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Case 1 — past_due: card declined, automation still running on grace.
  if (sub.status === "past_due") {
    return (
      <Banner tone="amber">
        <span>{t("subscriptionBanner.pastDue")}</span>
        <button
          type="button"
          className="ml-3 underline-offset-2 hover:underline"
          onClick={() => portalMutation.mutate()}
        >
          {t("subscriptionBanner.updateCard")}
        </button>
      </Banner>
    );
  }

  // Case 2 — no active subscription at all. Take them to the Settings page,
  // which has a BillingCard with the three plan CTAs that hit the same
  // checkout endpoint as the public landing.
  if (!sub.hasActiveSubscription) {
    return (
      <Banner tone="rose">
        <span>{t("subscriptionBanner.inactive")}</span>
        <button
          type="button"
          className="ml-3 underline-offset-2 hover:underline"
          onClick={() => navigate("/settings")}
        >
          {t("subscriptionBanner.pickPlan")}
        </button>
      </Banner>
    );
  }

  // Case 3 — trialing with fewer than 3 days left.
  if (sub.status === "trialing" && sub.trialEndsAt) {
    const remainingMs = new Date(sub.trialEndsAt).getTime() - Date.now();
    const remainingDays = Math.ceil(remainingMs / 86_400_000);
    if (remainingDays > 0 && remainingDays <= 3) {
      return (
        <Banner tone="brand">
          <span>
            {t("subscriptionBanner.trialEndingSoon", {
              date: formatDate(sub.trialEndsAt),
              count: remainingDays,
            })}
          </span>
        </Banner>
      );
    }
  }

  return null;
}

function Banner({
  tone,
  children,
}: {
  tone: "rose" | "amber" | "brand";
  children: React.ReactNode;
}): JSX.Element {
  const toneClass =
    tone === "rose"
      ? "border-rose-500/40 bg-rose-500/10 text-rose-100"
      : tone === "amber"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
      : "border-brand-400/40 bg-brand-500/10 text-brand-100";
  return (
    <div
      className={`mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${toneClass}`}
      role="status"
    >
      {children}
    </div>
  );
}
